import { ProductKind } from "./product-kind";

export const DEFAULT_LOCALE = "en";

/** One flat file: message key to ICU message. */
export type Messages = Readonly<Record<string, string>>;

export interface LocaleMessages {
  readonly base: Messages;
  readonly overlays: Readonly<Partial<Record<ProductKind, Messages>>>;
}

/** Every file for one namespace, keyed by locale. */
export type NamespaceBundle = Readonly<Record<string, LocaleMessages>>;

export interface NamespaceSchema {
  readonly namespace: string;
  /** Message key to the names of the arguments its ICU message takes. */
  readonly keys: Readonly<Record<string, readonly string[]>>;
}

export interface ResolveOptions {
  readonly locale: string;
  readonly productKind: ProductKind;
}

/** "fr-CA" falls back to "fr" before the default locale. */
const localeChain = (locale: string): string[] => {
  const chain = [DEFAULT_LOCALE];
  const language = locale.split("-")[0];
  if (language && language !== DEFAULT_LOCALE) chain.push(language);
  if (locale !== language && locale !== DEFAULT_LOCALE) chain.push(locale);
  return chain;
};

/**
 * Resolves one namespace to a flat message map. Later layers win, so the order
 * of precedence is: exact locale with product, exact locale, parent locale with
 * product, parent locale, default locale with product, default locale.
 */
export const resolveMessages = (
  bundle: NamespaceBundle,
  { locale, productKind }: ResolveOptions
): Messages => {
  const layers: Messages[] = [];
  for (const candidate of localeChain(locale)) {
    const messages = bundle[candidate];
    if (!messages) continue;
    layers.push(messages.base);
    const overlay = messages.overlays[productKind];
    if (overlay) layers.push(overlay);
  }
  return Object.assign({}, ...layers);
};
